import { useQuery } from '@tanstack/react-query';

import { healthService } from '@/services/health.service';

/**
 * Whether the API this client is talking to is running in development.
 *
 * NOT the same question as `import.meta.env.DEV`, which is a build-time fact
 * about the bundle. Running `npm run dev` against a production API - the normal
 * way this project is driven - left every dev-only control on screen while the
 * server refused the endpoints behind them (devReset.service gates on NODE_ENV
 * being exactly 'development'). A control that always errors is worse than no
 * control, so the ones that mint T&C accept links, open the lead review page or
 * reset counters ask the server instead.
 *
 * Defaults to FALSE - dev-only UI stays hidden while the answer is in flight and
 * if /health cannot be reached at all. Hiding a dev control wrongly costs a
 * developer one refresh; showing one wrongly puts a "reset the counters" button
 * in front of a live user.
 *
 * Unauthenticated (/health takes no token) and effectively immutable for the
 * life of the page, so it is fetched once and never refetched.
 */
export const useServerDevMode = (): boolean => {
  const { data } = useQuery({
    queryKey: ['health', 'dev-mode'],
    queryFn: healthService.getDevMode,
    // Gated, not merely ignored: without this a production bundle still fired
    // /health on every page load that mounts the admin shell or wizard step 3
    // and threw the answer away, while the comment below claimed otherwise.
    enabled: import.meta.env.DEV,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // `import.meta.env.DEV` stays as the OUTER condition too, so the answer cannot
  // turn dev UI on in a production bundle even if one were somehow cached.
  return import.meta.env.DEV && data === true;
};
